import { Timestamp } from '@angular/fire/firestore';

export type IScheduleDashboardStatus = 'overdue' | 'due-soon' | 'on-track';

/**
 * Schedule row for the dashboard scheduled stock takes panel.
 */
export interface IScheduleStatusRow {
  category: string;
  frequency: string;
  id: string;
  lastRunAt: Timestamp | null;
  name: string;
  nextDueLabel: string;
  overdueByDays: number;
  status: IScheduleDashboardStatus;
}

/**
 * Uncounted age band for the dashboard bar chart.
 */
export interface IUncountedBand {
  count: number;
  label: string;
  tone: 'neutral' | 'amber' | 'orange' | 'red' | 'red-dark';
}

/**
 * Top value item row for the dashboard table.
 */
export interface ITopValueItem {
  category: string;
  code: string;
  costPrice: number;
  daysSinceCounted: number | null;
  description: string;
  hasSchedule: boolean;
  lastCountedAt: Timestamp | null;
  stockOnHand: number;
  totalValue: number;
}

/**
 * Completed stock take summary for dashboard history panels.
 */
export interface IStockTakeHistoryRow {
  adjustmentValue: number;
  completedAt: Timestamp | null;
  /** Lines marked counted on the parent stock take document. */
  countedItems: number;
  id: string;
  /** Total item lines on the parent stock take document. */
  itemsCounted: number;
  name: string;
  varianceUnits: number;
}

export type IStockTakeDashboardKpiId =
  | 'overdue-schedules'
  | 'stale-open-stock-takes'
  | 'never-counted'
  | 'high-risk-items'
  | 'low-stock-value'
  | 'coverage-rate';

/**
 * KPI card on the stock take dashboard.
 */
export interface IStockTakeDashboardKpi {
  id: IStockTakeDashboardKpiId;
  label: string;
  sublabel: string;
  tone: 'danger' | 'warning' | 'success' | 'neutral' | 'primary';
  value: number | string;
  secondarySublabel?: string;
}

/**
 * Store-wide stock metrics for the dashboard (Firestore count + sum aggregation queries).
 */
export interface IStoreWideStockDashboardMetrics {
  /** Items with null/missing `_stock_take_date` (for Typesense index sync card — not never-counted). */
  absentDateFieldCount: number;
  bandsAvailable: boolean;
  countedItemsInCoveragePeriod: number;
  dateMetricsAvailable: boolean;
  /** Items with `_stock_value` of -R2,000 or more negative. */
  lowStockValueItemCount: number;
  /** Never-counted by dashboard cutoff (10+ years / invalid placeholders), excluding report-to items. */
  missingCount: number;
  /** Sum of `_stock_value` for items at or below -R2,000. */
  negativeStockValueTotal: number;
  neverCountedStockValue: number;
  /** Items whose report-item code is set. These cannot be counted and are excluded from date metrics. */
  reportToItemCount: number;
  /** Sum of `_stock_value` for report-to items. */
  reportToStockValue: number;
  totalStockCount: number;
  totalStockValue: number;
  uncountedBands: IUncountedBand[];
  valueCountedInCoveragePeriod: number;
}

/**
 * Aggregated dashboard payload stored in NgRx.
 */
export interface IStockTakeDashboardData {
  canAddSchedule: boolean;
  /** Stock items that do not report to another item. Last-counted metrics use this total. */
  countableStockCount: number;
  coverageRate: number;
  /** Firestore items with null/missing `_stock_take_date` (index sync card — not never-counted). */
  firestoreMissingStockTakeDateCount: number;
  firestoreTotalStockCount: number;
  hasActiveSchedules: boolean;
  highRiskTop100Count: number;
  highRiskTop100Value: number;
  kpis: IStockTakeDashboardKpi[];
  lowStockValueItemCount: number;
  negativeStockValueTotal: number;
  neverCountedStockValue: number;
  neverCountedStoreWide: number;
  /** Items with a report-item code. Shown separately so the gap versus total stock is visible. */
  reportToItemCount: number;
  scheduledTakes: IScheduleStatusRow[];
  stockTakeHistory: IStockTakeHistoryRow[];
  topValueItems: ITopValueItem[];
  topValueNotCounted30d: number;
  totalStockValue: number;
  typesenseMissingStockTakeDateCount: number;
  typesenseStockTakeDateSchemaReady: boolean;
  uncountedBands: IUncountedBand[];
  uncountedBandsMaxCount: number;
  valueCountedInCoveragePeriod: number;
}
